import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft } from "lucide-react";
import { propertyApi, associationApi } from "@/services/api";
import { Property } from "@/models";
import PropertyBulkFormContent from "@/components/properties/PropertyBulkFormContent";
import { extractDataArray } from "@/utils/apiUtils";

const PropertyBulkForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const searchParams = new URLSearchParams(location.search);
  const associationIdFromUrl = searchParams.get('association_id');
  const associationId = associationIdFromUrl ? parseInt(associationIdFromUrl) : undefined;

  const { data: associationsResponse = [], isLoading: isLoadingAssociations } = useQuery({
    queryKey: ['associations'],
    queryFn: () => associationApi.list(),
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading associations",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });
  
  const associations = extractDataArray(associationsResponse);
  
  const { data: association } = useQuery({
    queryKey: ['association', associationId],
    queryFn: () => associationId ? associationApi.getById(associationId) : null,
    enabled: !!associationId,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading association",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  const bulkCreateMutation = useMutation({
    mutationFn: async (properties: Omit<Property, "id" | "created_at" | "updated_at">[]) => {
      const results = [];
      
      for (const property of properties) {
        const result = await propertyApi.create(property);
        results.push(result);
      }
      
      return results;
    },
    onSuccess: (data) => {
      toast({
        title: "Properties created",
        description: `Successfully added ${data.length} properties.`
      });
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      
      if (associationId) {
        navigate(`/backoffice/associations/${associationId}/edit`);
      } else {
        navigate('/backoffice/properties');
      }
      setLoading(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error creating properties",
        description: error.message || "Failed to create properties",
        variant: "destructive"
      });
      setLoading(false);
    }
  });

  const handleSaveProperties = async (properties: Omit<Property, "id" | "created_at" | "updated_at">[]) => {
    if (properties.length === 0) {
      toast({
        title: "No properties to add",
        description: "Please add at least one property",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    bulkCreateMutation.mutate(properties);
  };

  const navigateBack = () => {
    if (associationId) {
      navigate(`/backoffice/associations/${associationId}/edit`);
    } else {
      navigate('/backoffice/properties');
    }
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={navigateBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              Add Multiple Properties {association && `to ${association.name}`}
            </h1>
          </div>
        </div>

        <PropertyBulkFormContent
          associations={associations}
          isLoading={loading || isLoadingAssociations}
          onSave={handleSaveProperties}
          showAssociationField={!associationId}
          associationId={associationId}
        />
      </div>
    </BackofficeLayout>
  );
};

export default PropertyBulkForm;
